/**
 * Job column definitions with JSX cell renderers
 *
 * Defines table columns using extracted cell components
 *
 * Migration: 2025-11-15 - Split sourceTarget into 3 inline columns
 * - type: Job type badge (job.source)
 * - source: Source entity name (job.name)
 * - target: Target DNS/IP (job.dns with parsed name)
 */

import { DashedTag } from '@praetorian-chariot/ui';

import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import { Column } from '@/components/table/types';
import { JobStatusCell } from '@/sections/jobs/components/cells';
import { stripJobSourceTimestamp } from '@/sections/jobs/utils';
import { Job } from '@/types';
import { formatDate } from '@/utils/date.util';

import { COLUMN_ALIGNMENT, COLUMN_LABELS } from './columns';

/**
 * Create job column definitions with cell renderers
 *
 * Migration: 2025-11-15 - Replaced JobSourceTargetCell with 3 inline columns
 * Following Asset table pattern for simple inline rendering
 *
 * @returns Array of column definitions for jobs table
 */
export const createJobColumns = (): Column<Job>[] => [
  {
    id: 'status',
    label: COLUMN_LABELS.status,
    ...getColumnWidth('job', 'status'),
    cell: job => <JobStatusCell job={job} />,
    className: '[&>span]:justify-center',
    align: COLUMN_ALIGNMENT.status,
  },
  {
    id: 'source',
    label: COLUMN_LABELS.type,
    ...getColumnWidth('job', 'type'),
    cell: (job: Job) => (
      <DashedTag size="xs" label={stripJobSourceTimestamp(job.source)} />
    ),
    sorting: true,
  },
  {
    id: 'name',
    label: COLUMN_LABELS.source,
    ...getColumnWidth('job', 'source'),
    cell: (job: Job) => job.name || '-',
    sorting: false,
  },
  {
    id: 'dns',
    label: COLUMN_LABELS.target,
    ...getColumnWidth('job', 'target'),
    cell: (job: Job) => {
      // Parse target hostname from job key (index 3)
      // job.key format: #job#123#port-scanning#example.com
      const targetHostname = job.key.split('#')[3];

      return (
        <NameColumn name={job.dns || '-'} description={targetHostname || ''} />
      );
    },
    sorting: false,
  },
  {
    id: 'created',
    label: COLUMN_LABELS.created,
    ...getColumnWidth('job', 'created'),
    cell: (job: Job) => formatDate(job.created, 'yyyy-MM-dd HH:mm', false),
    sorting: true,
  },
  {
    id: 'updated',
    label: COLUMN_LABELS.updated,
    ...getColumnWidth('job', 'updated'),
    cell: (job: Job) => formatDate(job.updated, 'yyyy-MM-dd HH:mm', false),
    sorting: true,
  },
];
